import React from 'react';
import { Link } from 'react-router-dom';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.12) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const ServiceCard = ({ icon, title, bullets }) => (
  <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:border-gold-200 hover:shadow-md transition-all">
    <div className="w-12 h-12 bg-gold-500/10 border border-gold-200 rounded-xl flex items-center justify-center mb-5">
      <span className="text-2xl">{icon}</span>
    </div>
    <h3 className="text-xl font-bold text-navy-800 font-heading mb-4">{title}</h3>
    <ul className="space-y-2.5">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-start gap-2.5 text-slate-600 text-sm">
          <span className="text-gold-500 mt-0.5 shrink-0 font-bold">›</span>
          {b}
        </li>
      ))}
    </ul>
  </div>
);

const SERVICES = [
  {
    icon: '📐',
    title: 'Space Planning & Interior Design',
    bullets: [
      'Systems furniture expertise — specifying, configuring, and optimizing MillerKnoll (Herman Miller, Knoll), Haworth, Steelcase, and more',
      'Precision drafting with AutoCAD and Visio — pinpoint-accurate floor plans and layouts tailored to your workflow',
      'Space utilization analysis, occupancy tracking, and headcount forecasting',
    ],
  },
  {
    icon: '🚚',
    title: 'Asset & Move Management',
    bullets: [
      'Turnkey move management — seamless coordination of internal re-stacks, department relocations, and new office setups',
      'Comprehensive inventory management — tracking and auditing physical assets, furniture, and equipment',
      'Minimal-disruption scheduling and post-move audit and issue resolution',
    ],
  },
  {
    icon: '🎤',
    title: 'Conference Center & Hospitality',
    bullets: [
      'End-to-end management of high-traffic conference centers and shared spaces',
      'Room reservations, scheduling, and conflict resolution for complex multi-use environments',
      'Dedicated on-site Audio-Visual (AV) support ensuring presentations, video conferences, and live events run without a hitch',
    ],
  },
  {
    icon: '🎧',
    title: 'Facilities Help Desk',
    bullets: [
      'Centralized, responsive point of contact for all maintenance, service requests, and hot/cold calls',
      'Work order creation, tracking, and closure with SLA-based performance reporting',
      'Vendor coordination, escalation management, and contractor oversight',
    ],
  },
  {
    icon: '🖥️',
    title: 'IWMS / CAFM / CMMS / EAMS',
    bullets: [
      'Data-driven Integrated Workplace Management Systems (IWMS) to track maintenance, analyze space utilization, and lower operational costs',
      'ARCHIBUS, TRIRIGA, and MAXIMO platform expertise — implementation, configuration, and ongoing administration',
      'Data migration, end-user training, and change management support',
    ],
  },
  {
    icon: '🪧',
    title: 'Wayfinding & Signage',
    bullets: [
      'Clear, professional interior and exterior signage solutions to improve navigation and brand presence',
      'Brand-compliant and agency-standard signage design and production',
      'ADA-compliant solutions with installation, maintenance, and updates',
    ],
  },
];

const FacilitiesManagement = () => {
  return (
    <div className="pt-20">

      {/* Page header */}
      <section className="relative bg-navy-800 py-20 overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gold-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to="/" className="hover:text-gold-400 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gold-400">Facilities Management</span>
          </nav>
          <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">Service Line</p>
          <h1 className="text-4xl md:text-5xl font-black text-white font-heading leading-tight max-w-3xl">
            Facilities Management &amp; Space Planning
          </h1>
          <p className="text-gold-400 font-semibold mt-4 mb-2">Seamless Operations. Intelligent Spaces. Maximized Efficiency.</p>
          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
            In today's dynamic environment, your workplace needs to be more than a physical location — it needs
            to be a strategic asset. DTX delivers comprehensive facilities support and cutting-edge space planning
            services that keep your agency running smoothly and your real estate optimized.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Our Capabilities</p>
            <h2 className="text-4xl font-black text-navy-800 font-heading">What We Deliver</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((s) => (
              <ServiceCard key={s.title} {...s} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Partner — Challenge/Solution */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Why DTX</p>
            <h2 className="text-3xl font-black text-navy-800 font-heading">Why Partner with DTX</h2>
            <p className="text-slate-500 mt-3">We don't just manage spaces — we optimize them.</p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
            <div className="grid grid-cols-2 bg-navy-800">
              <div className="py-3 px-6 text-xs font-bold text-gold-400 uppercase tracking-wider">The Challenge</div>
              <div className="py-3 px-6 text-xs font-bold text-gold-400 uppercase tracking-wider">Our Solution</div>
            </div>
            {[
              { challenge: 'Fragmented Vendors', solution: 'One dedicated partner handling everything from AV support to furniture lifecycle management.' },
              { challenge: 'Underutilized Real Estate', solution: 'Advanced IWMS and AutoCAD/Visio insights to maximize every square foot of your federal footprint.' },
              { challenge: 'Operational Downtime', solution: 'Highly coordinated move and help desk structures that keep your business moving without interruption.' },
            ].map(({ challenge, solution }, i) => (
              <div key={challenge} className={`grid grid-cols-2 border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                <div className="py-5 px-6 font-bold text-navy-800 text-sm border-r border-gray-100">{challenge}</div>
                <div className="py-5 px-6 text-slate-600 text-sm">{solution}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-800 py-16 relative overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black text-white font-heading mb-4">
            Need Facilities Support?
          </h2>
          <p className="text-slate-300 mb-8 max-w-lg mx-auto">
            Contact us to discuss your agency's facilities requirements and how DTX can deliver results.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-gold-500 hover:bg-gold-400 text-navy-900 px-8 py-4 rounded-xl font-bold transition-colors shadow-lg"
          >
            Get in Touch
          </Link>
        </div>
      </section>

    </div>
  );
};

export default FacilitiesManagement;
